import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { useRouter, usePathname, useSearchParams } from 'next/navigation';
import useSnackBar from '@hooks/useSnackbar';
import clearFiltersFromQuery from '@helpers/clearFiltersFromQuery';
import { isProductFilterCategoryKey } from '@helpers/productHelper';
import Heading from '@atoms/Heading/Heading';
import Icon from '@atoms/Icon/Icon';
import FilterList from '@molecules/FilterList/FilterList';
import {
  sizes,
  StyledOpenFilterButton,
  StyledProductListFilter,
  StyledProductListFilterTakeoverBody,
  StyledProductListFilterTakeoverButton,
  StyledProductListFilterTakeoverFooter,
  StyledProductListFilterTakeoverHeader,
  StyledProductListFilterWrapper,
} from './ProductListFilter.styles';
import useResponsive from '@hooks/useResponsive';
import type { BreadcrumbDataSearchStateData, FacetDataObject } from '@occ/api-client';
import trackProductListFilter from '@helpers/analyticsHelpers/trackProductListFilter';
import Filter from '@icons/filter.svg';
import SlideInSection from '@molecules/SlideInSection/SlideInSection';
import { toggleSlideInSection } from '@slices/slideInSectionSlice';
import { useAppDispatch } from '@hooks/useAppDispatch';

type Props = {
  totalNumberOfResults: number;
  breadcrumbs: Array<BreadcrumbDataSearchStateData>;
  facets?: FacetDataObject[];
  isLoading?: boolean;
  gtmTrackOnFilterOpened?: boolean;
};

const ProductListFilter = ({
  totalNumberOfResults = 0,
  breadcrumbs,
  facets,
  isLoading,
  gtmTrackOnFilterOpened = true,
}: Props) => {
  const [activeCategory, setActiveCategory] = useState('');
  const [showFilterSlideInSection, setShowFilterSlideInSection] = useState<boolean>(false);
  const { isMobile } = useResponsive();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const asPath = typeof window !== 'undefined' ? window.location.pathname + window.location.search : pathname || '';
  const isPromotionPage = !!pathname?.startsWith('/erbjudanden');
  const { setSnack } = useSnackBar();
  const dispatch = useAppDispatch();
  const t = useTranslations('productFilter');

  const getCategoryTranslation = (categoryCode: string): string => {
    if (isProductFilterCategoryKey(categoryCode)) {
      return t(`categories.${categoryCode}`);
    }
    // Fallback to the original code if no translation exists
    return categoryCode;
  };

  const addSnackWithData = () =>
    setSnack({
      icon: 'valid',
      text: t('resetFilterNotification'),
      bottomPosition: isMobile ? sizes.footerHeight + 20 : undefined,
    });

  const clearFilters = () => {
    const query = Object.fromEntries(searchParams?.entries() || []);
    const nextQuery = clearFiltersFromQuery(query);
    // Convert the object to URL params format
    const params = new URLSearchParams();
    Object.entries(nextQuery).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach((v) => params.append(key, v));
      } else if (value) {
        params.set(key, value);
      }
    });
    const queryString = params.toString();
    router.replace(queryString ? `?${queryString}` : pathname || '/');
    addSnackWithData();
  };

  return (
    <StyledProductListFilter $loading={isLoading} data-testid="productlist-filter">
      {!isLoading && facets && facets.length > 0 && (
        <StyledOpenFilterButton
          onClick={() => setShowFilterSlideInSection(true)}
          variant="secondary"
          size="small"
          $isPromotionPage={isPromotionPage}
          data-testid="toggle-filter-slide-in-section"
        >
          <Icon svg={Filter} size={16} />
          <span>{breadcrumbs?.length > 0 ? t('appliedFilters', { amount: breadcrumbs.length }) : t('filter')}</span>
        </StyledOpenFilterButton>
      )}

      {showFilterSlideInSection && (
        <SlideInSection
          onClose={() => {
            setShowFilterSlideInSection(false);
            dispatch(toggleSlideInSection(false));
          }}
          hasCloseIcon
          closeOnEscape
        >
          <StyledProductListFilterWrapper>
            <StyledProductListFilterTakeoverHeader>
              <Heading variant="h2" size="small">
                {t('title')}
              </Heading>
            </StyledProductListFilterTakeoverHeader>
            <StyledProductListFilterTakeoverBody>
              {facets &&
                facets?.length > 0 &&
                facets.map((facet) => {
                  return (
                    facet.values &&
                    facet?.values?.length > 0 && (
                      <FilterList
                        code={facet?.code as string}
                        name={getCategoryTranslation(facet.code ?? '')}
                        option={facet}
                        key={facet.name}
                        expandedListName={activeCategory}
                        toggleList={() => {
                          const activeCat = activeCategory === facet.name ? '' : facet.name;
                          if (activeCat && gtmTrackOnFilterOpened) {
                            trackProductListFilter(asPath, getCategoryTranslation(facet.code ?? ''));
                          }
                          setActiveCategory(activeCat as string);
                        }}
                        breadcrumbs={breadcrumbs}
                      />
                    )
                  );
                })}
            </StyledProductListFilterTakeoverBody>
            <StyledProductListFilterTakeoverFooter>
              <StyledProductListFilterTakeoverButton
                disabled={Object.keys(breadcrumbs).length <= 0}
                variant="secondary"
                onClick={clearFilters}
              >
                {t('clear')}
              </StyledProductListFilterTakeoverButton>
              <StyledProductListFilterTakeoverButton
                onClick={() => setShowFilterSlideInSection(false)}
                variant="primary"
              >
                {t('done', { amount: totalNumberOfResults })}
              </StyledProductListFilterTakeoverButton>
            </StyledProductListFilterTakeoverFooter>
          </StyledProductListFilterWrapper>
        </SlideInSection>
      )}
    </StyledProductListFilter>
  );
};

export default ProductListFilter;
